import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, Check } from "lucide-react";
import axios from "axios";
import NationalNav from "@/components/national/NationalNav";
import NationalFooter from "@/components/national/NationalFooter";
import { PARTNERSHIP_TYPES, PARTNERSHIP_BENEFITS, IMG } from "@/lib/platform";
import { trackLead } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CITY_OPTIONS = ["София", "Пловдив", "Варна", "Всички събития"];

const inputCls =
  "w-full px-5 py-3.5 rounded-xl bg-[#0B1F3D] border border-[#1E3A63] text-[#F5EBD9] placeholder:text-[#8A9EB8] focus:outline-none focus:border-[#F5892E] transition-colors";

export default function Partners() {
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    company: "", name: "", email: "", phone: "", website: "",
    types: [], cities: [], industry: "", budget: "", message: "", hp: "",
  });
  const [consent, setConsent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k, v) =>
    setForm({ ...form, [k]: form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v] });

  const stepValid = () => {
    if (step === 0)
      return form.company.trim() && form.name.trim() && /.+@.+\..+/.test(form.email) && form.phone.trim();
    if (step === 1) return form.types.length > 0;
    if (step === 2) return form.cities.length > 0;
    return consent;
  };

  const submit = async () => {
    if (status === "busy") return;
    setStatus("busy");
    setError("");
    try {
      const r = await axios.post(`${API}/partner-inquiry`, { ...form, consent });
      if (r.data?.success) {
        trackLead({ email: form.email, content_name: "Partner Inquiry", content_category: "partnership" });
        setStatus("done");
      } else {
        setStatus("error");
        setError("Провери полетата и опитай пак.");
      }
    } catch {
      setStatus("error");
      setError("Нещо се обърка. Опитай отново след малко.");
    }
  };

  const STEPS = ["Компания", "Партньорство", "Град", "Детайли"];

  return (
    <main data-testid="partners-page" className="relative bg-[#0B1F3D] text-[#F5EBD9] min-h-screen">
      <NationalNav />

      {/* Hero */}
      <section className="relative pt-32 sm:pt-44 pb-20 lg:pb-28 overflow-hidden">
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMG.arch2})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3D]/70 to-[#0B1F3D]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5892E] mb-4 flex items-center gap-4">
              <span className="h-px w-10 bg-[#F5892E]" />
              Партньорства
            </div>
            <h1 className="font-display uppercase text-4xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight max-w-5xl">
              Вашият бранд.<br />
              <span className="text-[#F5892E]">Пред хората, които движат имотния пазар.</span>
            </h1>
            <p className="mt-8 text-lg text-[#C8D4E6] leading-relaxed max-w-[62ch]">
              „Играта на имотите" събира на едно място инвеститори, купувачи, предприемачи,
              строители и професионалисти от сектора. Позиционирайте бизнеса си пред аудитория,
              която активно се интересува от недвижими имоти, финансиране, инвестиции и нови
              възможности.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={() => document.getElementById("partner-form")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="partners-hero-cta"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#F5892E] text-[#0B1F3D] font-bold text-sm uppercase tracking-widest hover:bg-[#F5EBD9] transition-colors"
              >
                Стани партньор на събитие
                <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
              </button>
              <button
                onClick={() => document.getElementById("partner-form")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="partners-hero-inquiry"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-[#F5EBD9]/30 text-[#F5EBD9] font-medium text-sm uppercase tracking-widest hover:bg-[#F5EBD9] hover:text-[#0B1F3D] transition-all"
              >
                Изпрати запитване
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative py-20 lg:py-28 bg-[#07152B]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-12 lg:mb-16"
          >
            Защо да станете <span className="text-[#F5892E]">партньор</span>
          </motion.h2>
          <div className="border-t border-[#1E3A63]">
            {PARTNERSHIP_BENEFITS.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                data-testid={`benefit-${i}`}
                className="grid grid-cols-12 gap-4 lg:gap-8 py-7 lg:py-9 border-b border-[#1E3A63] items-baseline hover:bg-[#0F284B]/40 transition-colors px-2 sm:px-4"
              >
                <div className="col-span-2 lg:col-span-1 font-mono text-sm text-[#F5892E] tracking-widest">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="col-span-10 lg:col-span-4 font-display uppercase text-xl sm:text-2xl lg:text-3xl tracking-tight">
                  {b.title}
                </h3>
                <p className="col-span-12 lg:col-span-7 text-[#C8D4E6] leading-relaxed">{b.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-step form */}
      <section id="partner-form" className="relative py-20 lg:py-28 scroll-mt-24">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight text-center mb-3">
              Изпратете <span className="text-[#F5892E]">запитване</span>
            </h2>
            <p className="text-center text-[#8A9EB8] text-sm mb-10">
              Отговаряме лично на всяко запитване.
            </p>

            {status === "done" ? (
              <div data-testid="partner-success" className="text-center border border-[#F5892E]/50 bg-[#F5892E]/10 rounded-2xl p-10">
                <CheckCircle2 className="w-14 h-14 text-[#F5892E] mx-auto mb-5" strokeWidth={1.6} />
                <h3 className="font-display uppercase text-2xl sm:text-3xl mb-3">Благодарим за интереса!</h3>
                <p className="text-[#C8D4E6] leading-relaxed">
                  Получихме Вашето запитване. Екипът на „Играта на имотите" ще се свърже с Вас.
                </p>
              </div>
            ) : (
              <div className="bg-[#0F284B] border border-[#1E3A63] rounded-2xl p-7 sm:p-10">
                {/* Progress */}
                <div className="flex items-center gap-2 mb-8" data-testid="partner-progress">
                  {STEPS.map((s, i) => (
                    <div key={s} className="flex-1">
                      <div
                        className={`h-1 rounded-full transition-colors duration-500 ${
                          i <= step ? "bg-[#F5892E]" : "bg-[#1E3A63]"
                        }`}
                      />
                      <div className={`mt-2 font-mono text-[9px] uppercase tracking-[0.2em] ${i <= step ? "text-[#F5892E]" : "text-[#8A9EB8]"}`}>
                        {i + 1}. {s}
                      </div>
                    </div>
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.35 }}
                  >
                    {step === 0 && (
                      <div className="space-y-4" data-testid="partner-step-0">
                        <input placeholder="Име на компанията *" value={form.company} onChange={set("company")} data-testid="partner-company" className={inputCls} />
                        <input placeholder="Име и фамилия *" value={form.name} onChange={set("name")} data-testid="partner-name" className={inputCls} />
                        <input type="email" placeholder="Служебен имейл *" value={form.email} onChange={set("email")} data-testid="partner-email" className={inputCls} />
                        <input type="tel" placeholder="Телефон *" value={form.phone} onChange={set("phone")} data-testid="partner-phone" className={inputCls} />
                        <input placeholder="Уебсайт (по желание)" value={form.website} onChange={set("website")} data-testid="partner-website" className={inputCls} />
                      </div>
                    )}

                    {step === 1 && (
                      <div data-testid="partner-step-1">
                        <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#8A9EB8] mb-4">
                          Какъв тип партньорство Ви интересува? *
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {PARTNERSHIP_TYPES.map((t) => (
                            <button
                              key={t}
                              onClick={() => toggle("types", t)}
                              data-testid={`partner-type-${t}`}
                              className={`flex items-center justify-between px-5 py-4 rounded-xl border text-left transition-all ${
                                form.types.includes(t)
                                  ? "border-[#F5892E] bg-[#F5892E]/15 text-[#F5EBD9]"
                                  : "border-[#1E3A63] text-[#C8D4E6] hover:border-[#F5892E]/50"
                              }`}
                            >
                              {t}
                              {form.types.includes(t) && <Check className="w-4 h-4 text-[#F5892E]" strokeWidth={2.6} />}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div data-testid="partner-step-2">
                        <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#8A9EB8] mb-4">
                          За кое събитие? *
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {CITY_OPTIONS.map((c) => (
                            <button
                              key={c}
                              onClick={() => toggle("cities", c)}
                              data-testid={`partner-city-${c}`}
                              className={`flex items-center justify-between px-5 py-4 rounded-xl border text-left transition-all ${
                                form.cities.includes(c)
                                  ? "border-[#F5892E] bg-[#F5892E]/15 text-[#F5EBD9]"
                                  : "border-[#1E3A63] text-[#C8D4E6] hover:border-[#F5892E]/50"
                              }`}
                            >
                              {c}
                              {form.cities.includes(c) && <Check className="w-4 h-4 text-[#F5892E]" strokeWidth={2.6} />}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {step === 3 && (
                      <div className="space-y-4" data-testid="partner-step-3">
                        <input placeholder="Индустрия / сектор" value={form.industry} onChange={set("industry")} data-testid="partner-industry" className={inputCls} />
                        <input placeholder="Приблизителен бюджет (по желание)" value={form.budget} onChange={set("budget")} data-testid="partner-budget" className={inputCls} />
                        <textarea
                          placeholder="Съобщение / предложение (по желание)"
                          value={form.message}
                          onChange={set("message")}
                          data-testid="partner-message"
                          rows={4}
                          className={`${inputCls} resize-none`}
                        />
                        <label className="flex items-start gap-3 text-xs text-[#8A9EB8] leading-relaxed cursor-pointer">
                          <input
                            type="checkbox"
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            data-testid="partner-consent"
                            className="mt-0.5 w-4 h-4 accent-[#F5892E] shrink-0"
                          />
                          Съгласен/на съм данните ми да бъдат обработени за целта на запитването,
                          съгласно политиката за поверителност. *
                        </label>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* honeypot */}
                <input
                  type="text"
                  value={form.hp}
                  onChange={(e) => setForm({ ...form, hp: e.target.value })}
                  tabIndex={-1}
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                />

                {status === "error" && (
                  <div className="mt-4 text-sm text-[#E5484D]" data-testid="partner-error">{error}</div>
                )}

                <div className="mt-8 flex items-center justify-between gap-4">
                  <button
                    onClick={() => setStep(Math.max(0, step - 1))}
                    disabled={step === 0}
                    data-testid="partner-back"
                    className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#8A9EB8] hover:text-[#F5892E] transition-colors disabled:opacity-30"
                  >
                    <ArrowLeft className="w-4 h-4" strokeWidth={2.2} />
                    Назад
                  </button>
                  {step < 3 ? (
                    <button
                      onClick={() => stepValid() && setStep(step + 1)}
                      disabled={!stepValid()}
                      data-testid="partner-next"
                      className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-[#F5892E] text-[#0B1F3D] font-bold text-sm uppercase tracking-widest hover:bg-[#F5EBD9] transition-colors disabled:opacity-40"
                    >
                      Напред
                      <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                    </button>
                  ) : (
                    <button
                      onClick={submit}
                      disabled={!stepValid() || status === "busy"}
                      data-testid="partner-submit"
                      className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-[#F5892E] text-[#0B1F3D] font-bold text-sm uppercase tracking-widest hover:bg-[#F5EBD9] transition-colors disabled:opacity-40"
                    >
                      {status === "busy" ? "Изпращам..." : "Изпрати запитване"}
                      <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                    </button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <NationalFooter />
    </main>
  );
}
